import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { Loading } from '../components/Loading';
import AppErrorState from '../components/AppErrorState';
import AppNavigation from '../components/AppNavigation';
import Login from './Login';
import Register from './Register';
import ForgotPassword from './ForgotPassword';
import Dashboard from './Dashboard';
import Movements from './Movements';
import Planning from './Planning';
import Profile from './Profile';
import FamilyMembers from './FamilyMembers';
import Categories from './Categories';
import Security from './Settings/Security';
import Appearance from './Settings/Appearance';
import SortSettings from './Settings/SortSettings';
import DeleteAccount from './DeleteAccount';
import { useFeedback } from '../FeedbackContext';
import { bootstrapAccountForUser } from '../services/legacyMigration';
import { clearPendingInvite, completeGoogleRedirect, getPendingInvite } from '../services/authService';
import AccountSwitcher from '../components/AccountSwitcher';
import CreateFamilyAccountSheet from '../components/CreateFamilyAccountSheet';
import { useUserAccounts } from '../hooks/useUserAccounts';
import { ACCOUNT_TYPES, createFinancialAccount, ensureUserDocument, selectCurrentAccount } from '../services/accountService';
import {
  getCurrentAccountDeletionState,
  resetAccountDeletionSnapshot,
} from '../services/accountDeletionService';

const getInviteErrorMessage = (code) => {
  if (code === 'INVITE_EXPIRED') return 'Este convite expirou. Solicite um novo link ao proprietário.';
  if (code === 'INVITE_ALREADY_USED') return 'Este convite já foi utilizado.';
  if (code === 'INVITE_NOT_FOUND') return 'O convite não existe ou foi revogado.';
  return 'Não foi possível aceitar o convite. Sua conta pessoal continua disponível.';
};

export const BOOTSTRAP_SLOW_THRESHOLD_MS = 7000;

export const isConnectivityBootstrapError = (error) => {
  const code = String(error?.code || error?.cause?.code || '').toLowerCase();
  return !navigator.onLine || ['unavailable', 'deadline-exceeded', 'network-request-failed']
    .some((candidate) => code.includes(candidate));
};

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(null);
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [bootstrapSlow, setBootstrapSlow] = useState(false);
  const [deletionState, setDeletionState] = useState({ state: 'idle', pending: false, canResume: false });
  const [accountSwitcherAnchor, setAccountSwitcherAnchor] = useState(null);
  const [showCreateFamilyAccount, setShowCreateFamilyAccount] = useState(false);
  const { showError, showSuccess } = useFeedback();
  const isDeletionPath = window.location.pathname === '/excluir-conta';
  const userAccounts = useUserAccounts(user?.uid, {
    enabled: Boolean(user && !loading && !bootstrapError && !deletionState.pending && !isDeletionPath),
  });
  const selectedAccountId = userAccounts.currentAccount?.id || null;
  const selectedRole = userAccounts.currentAccount?.role || null;
  const accountScopeKey = user && selectedAccountId ? `${user.uid}:${selectedAccountId}:${selectedRole || 'unknown'}` : 'no-account';

  useEffect(() => {
    setShowAccountSwitcher(false);
    setAccountSwitcherAnchor(null);
    setShowCreateFamilyAccount(false);
  }, [accountScopeKey]);

  useEffect(() => {
    if (!loading) {
      setBootstrapSlow(false);
      return undefined;
    }
    setBootstrapSlow(false);
    const timer = window.setTimeout(() => setBootstrapSlow(true), BOOTSTRAP_SLOW_THRESHOLD_MS);
    return () => window.clearTimeout(timer);
  }, [loading, user?.uid]);
  useEffect(() => {
    Promise.resolve(completeGoogleRedirect()).catch((error) => {
      if (error.code !== 'auth/popup-closed-by-user') {
        console.error('Erro ao concluir redirect do Google:', error);
        showError('Não foi possível concluir o login com Google. Tente novamente.', 'Falha no login');
      }
    });
  }, [showError]);

  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (!active) return;
      setLoading(true);
      setBootstrapError(null);
      setUser(nextUser);

      if (!nextUser) {
        resetAccountDeletionSnapshot();
        setDeletionState({ state: 'idle', pending: false, canResume: false });
        setLoading(false);
        return;
      }

      try {
        const remoteDeletionState = await getCurrentAccountDeletionState();
        if (!active) return;
        setDeletionState(remoteDeletionState);
        if (remoteDeletionState.pending || window.location.pathname === '/excluir-conta') return;
        const inviteToken = getPendingInvite();
        const account = await bootstrapAccountForUser(nextUser, inviteToken);
        if (!active) return;
        if (inviteToken) {
          clearPendingInvite();
          if (account.inviteAccepted) {
            showSuccess('A conta compartilhada já está disponível.', 'Convite aceito');
          } else if (account.inviteError) {
            showError(getInviteErrorMessage(account.inviteError), 'Convite não aceito');
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Erro ao preparar conta financeira:', {
            stage: error.stage || 'unknown',
            code: error.code || 'unknown',
          });
        }
        if (active) setBootstrapError(error);
      } finally {
        if (active) setLoading(false);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [showError, showSuccess]);

  if (loading) return <Loading slow={bootstrapSlow} />;

  if (user && deletionState.pending && !isDeletionPath) {
    return <DeleteAccount currentUser={user} pending />;
  }

  if (user && bootstrapError && !isDeletionPath) {
    const connectivityError = isConnectivityBootstrapError(bootstrapError);
    return (
      <AppErrorState
        fullPage
        variant={connectivityError ? 'connectivity' : 'bootstrap'}
        title="Não conseguimos preparar sua conta"
        description={connectivityError
          ? 'Tivemos um problema ao carregar suas informações. Isso pode acontecer quando a conexão está fraca. Seus dados continuam salvos e devem aparecer assim que a conexão for restabelecida.'
          : 'Tivemos um problema ao carregar suas informações. Seus dados continuam salvos. Tente novamente em alguns instantes.'
        }
        primaryLabel="Tentar novamente"
        onPrimaryAction={() => window.location.reload()}
        debugStage={process.env.NODE_ENV === 'development' ? bootstrapError.stage : undefined}
      />
    );
  }

  if (user && !isDeletionPath && !deletionState.pending && !bootstrapError && (
    userAccounts.selectingFallback
    || (userAccounts.status === 'resolving' && !userAccounts.currentAccount)
    || (userAccounts.status === 'ready' && userAccounts.accounts.length > 0 && !userAccounts.currentAccount)
  )) return <Loading />;

  if (user && !isDeletionPath && userAccounts.status === 'error' && userAccounts.accounts.length === 0) {
    return <AppErrorState
      fullPage
      variant="connectivity"
      title="Não foi possível confirmar suas contas"
      description="A conexão falhou durante a verificação de acesso. Nenhuma conta foi considerada removida. Tente novamente quando a conexão estiver estável."
      primaryLabel="Tentar novamente"
      onPrimaryAction={() => userAccounts.refresh().catch(() => {})}
    />;
  }

  if (user && !isDeletionPath && userAccounts.status === 'ready' && userAccounts.accounts.length === 0) {
    return <AppErrorState
      fullPage
      title="Nenhuma conta financeira disponível"
      description="Seu acesso às contas anteriores não está mais disponível. Nenhum dado dessas contas permanece aberto nesta sessão."
      primaryLabel="Verificar novamente"
      onPrimaryAction={() => userAccounts.refresh().catch(() => {})}
    />;
  }

  // Só troca os listeners financeiros depois que a conta foi confirmada pela descoberta de memberships.
  const protectedProps = {
    currentAccountId: selectedAccountId,
    currentRole: selectedRole,
    currentAccount: userAccounts.currentAccount,
    accounts: userAccounts.accounts,
    currentUser: user,
    onOpenAccountSwitcher: (anchorEl) => {
      const nextAnchor = anchorEl || null;
      if (showAccountSwitcher && accountSwitcherAnchor === nextAnchor) {
        setShowAccountSwitcher(false);
        setAccountSwitcherAnchor(null);
        return;
      }
      setAccountSwitcherAnchor(nextAnchor);
      setShowAccountSwitcher(true);
    },
  };

  return (
    <div className="Main">
      <Router>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
          <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/excluir-conta" element={<DeleteAccount currentUser={user} pending={deletionState.pending} />} />
          <Route path="/dashboard" element={user ? <Dashboard key={accountScopeKey} {...protectedProps} /> : <Navigate to="/login" />} />
          <Route path="/movimentacoes" element={user ? <Movements key={accountScopeKey} {...protectedProps} /> : <Navigate to="/login" />} />
          <Route path="/planejamento" element={user ? <Planning key={accountScopeKey} {...protectedProps} /> : <Navigate to="/login" />} />
          <Route path="/profile" element={user ? <Profile key={accountScopeKey} {...protectedProps} /> : <Navigate to="/login" />} />
          <Route path="/family-members" element={user ? <FamilyMembers key={accountScopeKey} {...protectedProps} /> : <Navigate to="/login" />} />
          <Route path="/categories" element={user ? <Categories key={accountScopeKey} {...protectedProps} /> : <Navigate to="/login" />} />

          <Route path="/security" element={user ? <Security currentUser={user} /> : <Navigate to="/login" />} />
          <Route path="/appearance" element={user ? <Appearance currentUser={user} /> : <Navigate to="/login" />} />
          <Route path="/sort-settings" element={user ? <SortSettings currentUser={user} /> : <Navigate to="/login" />} />
          <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} />} />
        </Routes>
        {user && !isDeletionPath && !deletionState.pending && <AppNavigation />}
        {user && !isDeletionPath && !deletionState.pending && showAccountSwitcher && (
          <AccountSwitcher
            accounts={userAccounts.accounts}
            currentAccountId={selectedAccountId}
            currentUser={user}
            loading={userAccounts.loading}
            onSelect={async (accountId) => {
              await ensureUserDocument(user);
              return userAccounts.selectAccount(accountId);
            }}
            onClose={() => {
              setShowAccountSwitcher(false);
              setAccountSwitcherAnchor(null);
            }}
            anchorEl={accountSwitcherAnchor}
            onCreateAccount={() => {
              setShowAccountSwitcher(false);
              setAccountSwitcherAnchor(null);
              setShowCreateFamilyAccount(true);
            }}
          />
        )}
        {user && !isDeletionPath && !deletionState.pending && showCreateFamilyAccount && (
          <CreateFamilyAccountSheet
            onClose={() => setShowCreateFamilyAccount(false)}
            onCreate={async (name) => {
              const created = await createFinancialAccount({
                uid: user.uid,
                name,
                type: ACCOUNT_TYPES.FAMILY,
              });

              let followUpError = null;
              try {
                await ensureUserDocument(user);
                await selectCurrentAccount(user.uid, created.accountId);
              } catch (selectionError) {
                followUpError = selectionError;
                if (process.env.NODE_ENV === 'development') {
                  console.error('Conta familiar criada; falha na seleção:', {
                    stage: 'select-created-family-account',
                    code: selectionError?.code || selectionError?.message || 'unknown',
                  });
                }
              }

              try {
                await userAccounts.refresh();
              } catch (refreshError) {
                followUpError = followUpError || refreshError;
                if (process.env.NODE_ENV === 'development') {
                  console.error('Conta familiar criada; falha ao atualizar lista:', {
                    stage: 'refresh-created-family-account',
                    code: refreshError?.code || refreshError?.message || 'unknown',
                  });
                }
              }

              if (followUpError) {
                showError('A conta foi criada, mas não foi possível selecioná-la agora. Reabra o seletor de contas para tentar novamente.', 'Conta criada');
                return;
              }
              showSuccess('A conta familiar já está disponível no seletor.', 'Conta familiar criada');
            }}
          />
        )}
      </Router>
    </div>
  );
}

export default AppRoutes;
